
import { UserProfile, BusinessRuleResult } from '../types';
import { adminService } from '@/core/services/AdminService';

// ============================================
// BIO VALIDATION CONSTANTS
// ============================================

/**
 * Comprimento mínimo de caracteres para uma biografia válida
 * Biografias abaixo deste valor não podem ser salvas
 */
export const BIO_MIN_LENGTH = 30;

/**
 * Comprimento máximo de caracteres para uma biografia
 * Limite técnico do textarea e do banco de dados
 */
export const BIO_MAX_LENGTH = 500;

/**
 * Score mínimo de IA para permitir salvamento direto
 * Biografias com score >= 70 são salvas sem modal de confirmação
 */
export const BIO_AI_SCORE_PASS_THRESHOLD = 70;

/**
 * Score mínimo de IA para mostrar warning ao invés de bloquear
 * Scores entre 40-69 mostram modal de warning mas permitem salvar
 */
export const BIO_AI_SCORE_WARNING_THRESHOLD = 40;

/**
 * Score mínimo de IA para considerar que a bio "pode ser salva"
 * Usado em conjunto com isWellWritten para validações
 */
export const BIO_AI_SCORE_CAN_SAVE = 50;

/**
 * Score mínimo de IA para considerar que o usuário "pode prosseguir"
 * Usado em conjunto com isWellWritten para fluxos de validação
 */
export const BIO_AI_SCORE_CAN_PROCEED = 60;

/**
 * Score que indica detecção de contatos externos (email, telefone, redes sociais)
 * Score negativo bloqueia absolutamente o salvamento
 */
export const BIO_AI_SCORE_BLOCKED = 0;

// ============================================
// BIO CATEGORIZATION THRESHOLDS
// ============================================

/**
 * Thresholds para categorização visual da biografia
 * Usados para exibir indicadores de qualidade na UI
 */
export const BIO_CATEGORY_THRESHOLDS = {
  /** Score >= 80: "ótimo" (verde, ✨) */
  EXCELLENT: 80,
  /** Score >= 60: "bom" (azul, 👍) */
  GOOD: 60,
  /** Score >= 40: "razoável" (amarelo, ⚠️) */
  FAIR: 40,
  /** Score < 40: "precisa melhorar" (vermelho, ❌) */
  POOR: 0
} as const;

export class ValidationRules {
  static validateProfileCompleteness(user: UserProfile): BusinessRuleResult {
    const requiredFields = ['role'];
    const missingFields = requiredFields.filter(field => !user[field as keyof UserProfile]);

    if (missingFields.length > 0) {
      return {
        allowed: false,
        reason: `Campos obrigatórios em falta: ${missingFields.join(', ')}`,
        data: { missingFields }
      };
    }

    return {
      allowed: true,
      reason: 'Perfil está completo',
      data: { completeness: 100 }
    };
  }

  static validatePlanChange(user: UserProfile, newPlan: string): BusinessRuleResult {
    const validPlans = ['sponsor_free', 'sponsor_standard', 'sponsor_premium', 'sponsored_free', 'sponsored_premium'];
    
    if (!validPlans.includes(newPlan)) {
      return {
        allowed: false,
        reason: `Plano '${newPlan}' não é válido. Planos disponíveis: ${validPlans.join(', ')}`
      };
    }

    if (user.plan_type === newPlan) {
      return {
        allowed: false,
        reason: 'Usuário já possui este plano'
      };
    }

    return {
      allowed: true,
      reason: `Mudança de plano de '${user.plan_type}' para '${newPlan}' é válida`
    };
  }

  static async validateInteractionLimits(user: UserProfile, action: string, currentCount: number): Promise<BusinessRuleResult> {
    // Admins bypasses all limitations
    const isAdmin = await adminService.isAdmin(user.id);
    if (isAdmin) {
      return {
        allowed: true,
        reason: 'Administrador tem acesso ilimitado',
        data: { 
          limit: -1, 
          current: currentCount, 
          remaining: -1 
        },
        metadata: { adminBypass: true }
      };
    }

    const limits: Record<string, Record<string, number>> = {
      sponsor_free: {
        daily_messages: 5,
        daily_connections: 5,
        daily_profile_views: 50
      },
      sponsor_standard: {
        daily_messages: -1, // Ilimitado
        daily_connections: -1,
        daily_profile_views: -1
      },
      sponsor_premium: {
        daily_messages: -1, // Ilimitado
        daily_connections: -1,
        daily_profile_views: -1
      },
      sponsored_free: {
        daily_messages: -1, // Ilimitado
        daily_connections: -1,
        daily_profile_views: -1
      },
      sponsored_premium: {
        daily_messages: -1, // Ilimitado
        daily_connections: -1,
        daily_profile_views: -1
      }
    };

    const planLimits = limits[user.plan_type] || limits.sponsor_free;
    const actionLimit = planLimits[action];

    if (actionLimit === undefined) {
      return {
        allowed: true,
        reason: `Ação '${action}' não tem limite definido`
      };
    }

    if (actionLimit === -1) {
      return {
        allowed: true,
        reason: 'Ação ilimitada para este plano',
        data: { 
          limit: -1, 
          current: currentCount, 
          remaining: -1,
          isUnlimited: true 
        }
      };
    }

    if (currentCount >= actionLimit) {
      return {
        allowed: false,
        reason: `Limite diário atingido para '${action}' (${actionLimit})`,
        data: { limit: actionLimit, current: currentCount }
      };
    }

    return {
      allowed: true,
      reason: `Ação permitida (${currentCount}/${actionLimit})`,
      data: { limit: actionLimit, current: currentCount, remaining: actionLimit - currentCount }
    };
  }

  static async canViewConnections(user: UserProfile): Promise<BusinessRuleResult> {
    // Admins bypasses all restrictions
    const isAdmin = await adminService.isAdmin(user.id);
    if (isAdmin) {
      return {
        allowed: true,
        reason: 'Administrador tem acesso total',
        data: { 
          showBlur: false
        },
        metadata: { adminBypass: true }
      };
    }

    // sponsor_free users should see blurred overlay
    if (user.plan_type === 'sponsor_free') {
      return {
        allowed: false,
        reason: 'Usuário precisa de plano Standard ou Premium para visualizar conexões sem restrições',
        data: { 
          showBlur: true,
          requiredPlans: ['sponsor_standard', 'sponsor_premium']
        }
      };
    }

    // All other plans have full access
    return {
      allowed: true,
      reason: 'Usuário tem acesso completo às conexões',
      data: { showBlur: false }
    };
  }

  static async canViewNotificationDetails(user: UserProfile, notificationType: string): Promise<BusinessRuleResult> {
    // Admins bypasses all restrictions
    const isAdmin = await adminService.isAdmin(user.id);
    if (isAdmin) {
      return {
        allowed: true,
        reason: 'Administrador tem acesso total',
        data: { 
          showPromotional: false
        },
        metadata: { adminBypass: true }
      };
    }

    // sponsor_free users should see promotional content for profile_view notifications
    if (user.plan_type === 'sponsor_free' && notificationType === 'profile_view') {
      return {
        allowed: false,
        reason: 'Usuário sponsor_free precisa fazer upgrade para ver detalhes de visualizações',
        data: { 
          showPromotional: true,
          promotionalTitle: 'Um usuário te visitou!',
          promotionalMessage: 'Quer saber quem foi? Faça o upgrade agora!',
          redirectTo: '/main/subscriptions',
          blurAvatar: true
        }
      };
    }

    // All other cases have full access
    return {
      allowed: true,
      reason: 'Usuário tem acesso completo aos detalhes da notificação',
      data: { showPromotional: false, blurAvatar: false }
    };
  }

  /**
   * Valida se o usuário possui o número mínimo de fotos necessário
   * Requisito universal: >= 3 fotos
   */
  static validateMinimumPhotos(user: { profile_photos?: string[] }): BusinessRuleResult {
    const photos = user.profile_photos || [];
    const currentCount = photos.length;
    
    if (currentCount < MINIMUM_PHOTOS_REQUIRED) {
      const photosNeeded = MINIMUM_PHOTOS_REQUIRED - currentCount;
      const photoText = photosNeeded === 1 ? 'uma foto' : photosNeeded === 2 ? 'duas fotos' : 'três fotos';
      
      return {
        allowed: false,
        reason: `Adicione mais ${photoText} para completar seu perfil`,
        data: { 
          required: MINIMUM_PHOTOS_REQUIRED, 
          current: currentCount,
          missing: photosNeeded 
        }
      };
    }

    return {
      allowed: true,
      reason: 'Requisito de fotos atendido',
      data: { 
        required: MINIMUM_PHOTOS_REQUIRED, 
        current: currentCount 
      }
    };
  }

  /**
   * Verifica se o perfil está completo com TODOS os campos disponíveis em /main/profile
   * Seções: Fotos, Bio, Carreira, Aparência Física, Estilo de Vida, Relacionamentos
   */
  static validateProfileComplete(profile: {
    bio?: string | null;
    bio_score?: number | null;
    occupation?: string | null;
    education?: string | null;
    height?: string | null;
    body_type?: string | null;
    hair_color?: string | null;
    eye_color?: string | null;
    smoker?: string | null;
    drinker?: string | null;
    piercings?: string | null;
    tattoos?: string | null;
    children?: string | null;
    relationship_type?: string | null;
    travel_availability?: string | null;
    marital_status?: string | null;
    sigilo_presenca?: string | null;
    profile_photos?: string[] | null;
    main_photo_url?: string | null;
  }): BusinessRuleResult {
    const missingSections: string[] = [];
    
    // 1. Validar Fotos (mínimo 3)
    const photoCount = profile.profile_photos?.length || 0;
    if (photoCount < MINIMUM_PHOTOS_REQUIRED) {
      missingSections.push('Fotos');
    }
    
    // 2. Validar Bio (texto + score de IA)
    if (!profile.bio || profile.bio.length < BIO_MIN_LENGTH) {
      missingSections.push('Bio');
    } else if (profile.bio_score === null || profile.bio_score === undefined) {
      missingSections.push('Bio (validação IA)');
    }
    
    // 3. Carreira (occupation obrigatório)
    if (!profile.occupation) {
      missingSections.push('Carreira');
    }
    
    // 4. Aparência (TODOS os campos obrigatórios)
    if (!profile.height || !profile.body_type || !profile.hair_color || !profile.eye_color) {
      missingSections.push('Aparência');
    }
    
    // 5. Estilo de vida (TODOS os campos obrigatórios)
    if (!profile.smoker || !profile.drinker || !profile.tattoos || !profile.piercings || !profile.children) {
      missingSections.push('Estilo de Vida');
    }
    
    // 6. Relacionamentos (relationship_type + marital_status obrigatórios)
    if (!profile.relationship_type || !profile.marital_status) {
      missingSections.push('Relacionamentos');
    }

    const totalSections = 6;
    const completedSections = totalSections - missingSections.length;
    const completionPercentage = Math.round((completedSections / totalSections) * 100);

    if (missingSections.length > 0) {
      return {
        allowed: false,
        reason: `Perfil incompleto: ${missingSections.join(', ')}`,
        data: { missingSections, completionPercentage }
      };
    }

    return {
      allowed: true,
      reason: 'Perfil completo',
      data: { missingSections: [], completionPercentage: 100 }
    };
  }

  /**
   * Verifica se o usuário pode acessar o chat baseado nas novas regras:
   * - Sponsored: precisa de ≥3 fotos
   * - Sponsor: Premium libera direto, senão precisa perfil completo + fully_verified
   */
  static canAccessChat(profile: {
    role?: string | null;
    plan_type?: string;
    profile_photos?: string[] | null;
    verification_level?: string | null;
    bio?: string | null;
    bio_score?: number | null;
    occupation?: string | null;
    education?: string | null;
    height?: string | null;
    body_type?: string | null;
    hair_color?: string | null;
    eye_color?: string | null;
    smoker?: string | null;
    drinker?: string | null;
    piercings?: string | null;
    tattoos?: string | null;
    children?: string | null;
    relationship_type?: string | null;
    travel_availability?: string | null;
    marital_status?: string | null;
    sigilo_presenca?: string | null;
  }): BusinessRuleResult {
    const role = profile.role;
    const isPremium = profile.plan_type === 'sponsor_premium' || profile.plan_type === 'sponsored_premium';
    const hasMinPhotos = (profile.profile_photos?.length || 0) >= MINIMUM_PHOTOS_REQUIRED;
    const isFullyVerified = profile.verification_level === 'full';

    // SPONSORED: só precisa de ≥3 fotos
    if (role === 'sponsored') {
      if (!hasMinPhotos) {
        const missing = MINIMUM_PHOTOS_REQUIRED - (profile.profile_photos?.length || 0);
        return {
          allowed: false,
          reason: `Adicione mais ${missing} foto(s) ao seu perfil`,
          data: {
            actions: [
              { label: 'Adicionar Fotos', url: '/main/complete-profile', primary: true, highlight: false }
            ]
          }
        };
      }
      return { allowed: true, reason: 'Acesso liberado' };
    }

    // SPONSOR
    // 1. Premium: libera direto
    if (isPremium) {
      return { allowed: true, reason: 'Acesso Premium liberado' };
    }

    // 2. Perfil completo?
    const profileComplete = ValidationRules.validateProfileComplete(profile);
    if (!profileComplete.allowed) {
      return {
        allowed: false,
        reason: 'Complete seu perfil para conversar',
        data: {
          missingSections: profileComplete.data?.missingSections || [],
          actions: [
            { label: 'Assinar Premium', url: '/main/subscriptions', primary: true, highlight: true },
            { label: 'Completar Perfil', url: '/main/complete-profile', primary: false, highlight: false }
          ]
        }
      };
    }

    // 3. Verificado (fully_verified)?
    if (!isFullyVerified) {
      return {
        allowed: false,
        reason: 'Verifique seu perfil para conversar',
        data: {
          actions: [
            { label: 'Assinar Premium', url: '/main/subscriptions', primary: true, highlight: true },
            { label: 'Verificar Perfil', url: '/main/verify', primary: false, highlight: false }
          ]
        }
      };
    }

    return { allowed: true, reason: 'Acesso liberado' };
  }

  /**
   * Verifica se o usuário está completamente verificado
   * NOTA: Retorna status de verificação mas NÃO bloqueia envio de mensagens
   * O bloqueio foi removido - banners informativos são exibidos no chat
   */
  static validateProfileVerified(profile: {
    verification_level?: string;
  }): BusinessRuleResult {
    const isVerified = profile.verification_level === 'full';
    
    return {
      allowed: isVerified,
      reason: isVerified 
        ? 'Usuário totalmente verificado' 
        : 'Usuário não verificado - banner informativo será exibido',
      data: { 
        isVerified,
        verificationLevel: profile.verification_level || 'basic',
        showWarningBanner: !isVerified
      }
    };
  }

  /**
   * Verifica status de verificação para envio de mensagens
   * NOVA REGRA: Verificação NÃO bloqueia envio, apenas mostra banners informativos
   */
  static canSendMessageWithVerificationStatus(profile: {
    verification_level?: string;
  }): BusinessRuleResult {
    const isVerified = profile.verification_level === 'full';
    
    // Sempre permite envio - verificação é apenas informativa
    return {
      allowed: true,
      reason: isVerified 
        ? 'Usuário verificado' 
        : 'Usuário não verificado - banner informativo será exibido',
      data: { 
        isVerified,
        verificationLevel: profile.verification_level || 'basic',
        showWarningBanner: !isVerified
      }
    };
  }

  /**
   * Valida o comprimento e qualidade de uma biografia
   * @param bio - Texto da biografia
   * @param aiScore - Score opcional da avaliação de IA (0-100 ou negativo se contatos externos)
   * @param isWellWritten - Flag opcional da IA indicando se o texto está bem escrito
   * @returns Resultado da validação com detalhes sobre o que está incorreto
   */
  static validateBioContent(
    bio: string,
    aiScore?: number,
    isWellWritten?: boolean
  ): BusinessRuleResult {
    const trimmedBio = bio.trim();
    
    // 1. Validar comprimento mínimo
    if (trimmedBio.length < BIO_MIN_LENGTH) {
      const charsNeeded = BIO_MIN_LENGTH - trimmedBio.length;
      return {
        allowed: false,
        reason: `Biografia muito curta. Adicione mais ${charsNeeded} caracteres (mínimo: ${BIO_MIN_LENGTH})`,
        data: { 
          required: BIO_MIN_LENGTH,
          current: trimmedBio.length,
          missing: charsNeeded,
          validationType: 'length'
        }
      };
    }
    
    // 2. Validar comprimento máximo
    if (trimmedBio.length > BIO_MAX_LENGTH) {
      const charsOver = trimmedBio.length - BIO_MAX_LENGTH;
      return {
        allowed: false,
        reason: `Biografia muito longa. Remova ${charsOver} caracteres (máximo: ${BIO_MAX_LENGTH})`,
        data: { 
          max: BIO_MAX_LENGTH,
          current: trimmedBio.length,
          over: charsOver,
          validationType: 'length'
        }
      };
    }
    
    // 3. Validar score de IA (se fornecido)
    if (aiScore !== undefined) {
      // Bloqueia absolutamente se contatos externos detectados
      if (aiScore < BIO_AI_SCORE_BLOCKED) {
        return {
          allowed: false,
          reason: 'Contatos externos detectados. Remova emails, telefones ou redes sociais.',
          data: { 
            score: aiScore,
            blockType: 'external_contacts',
            validationType: 'ai_content'
          }
        };
      }
      
      // Bloqueia se score muito baixo
      if (aiScore < BIO_AI_SCORE_WARNING_THRESHOLD) {
        return {
          allowed: false,
          reason: `Qualidade da biografia abaixo do aceitável (${aiScore}/100). Melhore o conteúdo.`,
          data: { 
            score: aiScore,
            threshold: BIO_AI_SCORE_WARNING_THRESHOLD,
            blockType: 'low_quality',
            validationType: 'ai_quality'
          }
        };
      }
      
      // Warning se score médio (permite salvar com confirmação)
      if (aiScore < BIO_AI_SCORE_PASS_THRESHOLD) {
        return {
          allowed: true,
          reason: `Qualidade da biografia pode melhorar (${aiScore}/100). Considere adicionar mais detalhes.`,
          data: { 
            score: aiScore,
            threshold: BIO_AI_SCORE_PASS_THRESHOLD,
            requiresConfirmation: true,
            validationType: 'ai_quality'
          }
        };
      }
    }
    
    // 4. Tudo válido
    return {
      allowed: true,
      reason: 'Biografia válida',
      data: {
        length: trimmedBio.length,
        score: aiScore,
        isWellWritten,
        validationType: 'complete'
      }
    };
  }

  /**
   * Determina se a biografia pode ser salva com base nas regras de IA
   * @param aiScore - Score da avaliação de IA (0-100 ou negativo)
   * @param isWellWritten - Flag da IA indicando se o texto está bem escrito
   * @returns true se pode salvar, false caso contrário
   */
  static canSaveBio(aiScore: number, isWellWritten: boolean): boolean {
    return aiScore >= BIO_AI_SCORE_BLOCKED && isWellWritten && aiScore >= BIO_AI_SCORE_CAN_SAVE;
  }

  /**
   * Determina se o usuário pode prosseguir com a biografia atual
   * @param aiScore - Score da avaliação de IA (0-100 ou negativo)
   * @param isWellWritten - Flag da IA indicando se o texto está bem escrito
   * @returns true se pode prosseguir, false caso contrário
   */
  static canProceedWithBio(aiScore: number, isWellWritten: boolean): boolean {
    return aiScore >= BIO_AI_SCORE_BLOCKED && isWellWritten && aiScore >= BIO_AI_SCORE_CAN_PROCEED;
  }

  /**
   * Retorna o tipo de modal que deve ser exibido ao tentar salvar
   * @param aiScore - Score da avaliação de IA (0-100 ou negativo)
   * @returns 'none' | 'block' | 'warning'
   */
  static getBioSaveModalType(aiScore: number): 'none' | 'block' | 'warning' {
    if (aiScore < BIO_AI_SCORE_BLOCKED) return 'block'; // Contatos externos
    if (aiScore < BIO_AI_SCORE_WARNING_THRESHOLD) return 'block'; // Qualidade muito baixa
    if (aiScore < BIO_AI_SCORE_PASS_THRESHOLD) return 'warning'; // Qualidade média
    return 'none'; // Qualidade boa
  }
}

/**
 * Número mínimo de fotos necessário para usar a plataforma
 */
export const MINIMUM_PHOTOS_REQUIRED = 3;
